/**
 * 全局图片失败处理：加载失败时不显示浏览器默认「裂开」图标与 alt 文案。
 * - 使用 1×1 黑色 PNG（缩放后铺满容器），不依赖透明图 + 背景。
 * - React 重渲染会把 src 再次写成失败 URL，故对已降级过的 img 监听 src 变化并写回占位。
 * 组件内自行 onError 切换占位 UI 的 img 请加 data-mg-native-fallback。
 */

const BLACK_PNG_1PX =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhKmMIQAAAABJRU5ErkJggg==";

let installed = false;

const shouldSkip = img =>
  typeof img?.hasAttribute === "function" &&
  img.hasAttribute("data-mg-native-fallback");

const isOurPlaceholderSrc = s =>
  typeof s === "string" && s.startsWith("data:image/png;base64,");

const applyBrokenFallback = img => {
  if (!img || img.tagName !== "IMG" || shouldSkip(img)) return;
  try {
    img.removeAttribute("srcset");
  } catch (e) {
    // ignore
  }
  img.dataset.mgBrokenFallback = "1";
  img.src = BLACK_PNG_1PX;
  img.alt = "";
};

const reassertBrokenPlaceholder = img => {
  if (!img || img.tagName !== "IMG" || img.dataset.mgBrokenFallback !== "1") {
    return;
  }
  if (shouldSkip(img)) return;
  const attr = img.getAttribute("src");
  if (!attr || isOurPlaceholderSrc(attr)) return;
  applyBrokenFallback(img);
};

const bindImg = img => {
  if (!img || img.tagName !== "IMG" || img.dataset.mgImgFallbackBound === "1") {
    return;
  }
  img.dataset.mgImgFallbackBound = "1";
  if (shouldSkip(img)) {
    return;
  }

  const onError = () => {
    img.removeEventListener("error", onError);
    if (shouldSkip(img)) return;
    applyBrokenFallback(img);
  };
  img.addEventListener("error", onError, { passive: true });

  if (
    img.complete &&
    img.naturalWidth === 0 &&
    img.src &&
    !isOurPlaceholderSrc(String(img.src))
  ) {
    onError();
  }
};

const scanNode = root => {
  if (!root || root.nodeType !== 1) return;
  if (root.tagName === "IMG") bindImg(root);
  root.querySelectorAll?.("img").forEach(bindImg);
};

export function installGlobalImageFallback() {
  if (typeof window === "undefined" || installed) return;
  installed = true;

  scanNode(document.documentElement);

  const observer = new MutationObserver(mutations => {
    for (const m of mutations) {
      if (m.type === "attributes" && m.attributeName === "src") {
        const t = m.target;
        if (t && t.tagName === "IMG") reassertBrokenPlaceholder(t);
        continue;
      }
      if (m.type === "childList") {
        for (const n of m.addedNodes) {
          if (n.nodeType === 1) scanNode(n);
        }
      }
    }
  });
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["src"],
  });
}
